import { NullPointerException } from '../lang/nullpointerexception';
import { Comparable } from './comparable';
import { Comparator } from './comparator';

export enum RedBlackColor {
  RED,
  BLACK,
}
export class RedBlackTreeNode<V> {
  value: V;
  left: RedBlackTreeNode<V> | null;
  right: RedBlackTreeNode<V> | null;
  parent: RedBlackTreeNode<V> | null;
  color: RedBlackColor;

  constructor(value: V, parent?: RedBlackTreeNode<V> | null) {
    this.value = value;
    if (parent === undefined) {
      parent = null;
    }
    this.parent = parent;
    this.left = this.right = null;
    this.color = RedBlackColor.BLACK;
  }
}

export class RedBlackTree<V> {
  private m_Root: RedBlackTreeNode<V> | null;
  private m_Comparator: Comparator<V> | null;
  private m_Size = 0;
  public modCount = 0;

  constructor(comparator?: Comparator<V>) {
    if (comparator === null) {
      throw new NullPointerException();
    }
    this.m_Comparator = comparator ?? null;
    this.m_Root = null;
  }

  public comparator() {
    return this.m_Comparator;
  }

  public contains(value: V): boolean {
    return this.getRedBlackTreeNode(value) !== null;
  }

  public remove(value: V): V | null {
    const p = this.getRedBlackTreeNode(value);
    if (p !== null) {
      this.deleteEntry(p);
      this.modCount++;
      this.m_Size--;
      return p.value;
    }
    return null;
  }

  public clear(): void {
    this.m_Root = null;
  }

  public put(value: V): V | null {
    let t = this.m_Root;
    if (t === null) {
      this.m_Root = new RedBlackTreeNode(value);
      this.m_Size = 1;
      this.modCount++;
      return null;
    }
    let cmp = 0;
    let parent: RedBlackTreeNode<V> = t;
    const cpr = this.m_Comparator;
    if (cpr !== null) {
      do {
        parent = t;
        cmp = cpr.compare(value, t.value);
        if (cmp < 0) {
          t = t.left;
        } else if (cmp > 0) {
          t = t.right;
        } else {
          return value;
        }
      } while (t !== null);
    } else {
      const k = value as unknown as Comparable<V>;
      do {
        parent = t;
        cmp = k.compareTo(t.value);
        if (cmp < 0) {
          t = t.left;
        } else if (cmp > 0) {
          t = t.right;
        } else {
          return value;
        }
      } while (t !== null);
    }
    const e = new RedBlackTreeNode<V>(value, parent);
    if (cmp < 0) {
      parent.left = e;
    } else {
      parent.right = e;
    }
    this.fixAfterInsertion(e);
    this.modCount++;
    this.m_Size++;
    return null;
  }

  public getPredecessor(value: V): V | null {
    const node = this.getRedBlackTreeNode(value);
    if (node === null) {
      return null;
    }
    const pred: RedBlackTreeNode<V> | null = this.predecessor(node);
    return RedBlackTree.entryOrNull(pred);
  }

  public getSuccessor(value: V): V | null {
    const node = this.getRedBlackTreeNode(value);
    if (node === null) {
      return null;
    }
    const pred: RedBlackTreeNode<V> | null = this.successor(node);
    return RedBlackTree.entryOrNull(pred);
  }

  public getFirstNode(): RedBlackTreeNode<V> | null {
    let p = this.m_Root;
    if (p === null) {
      return null;
    }
    while (p.left !== null) {
      p = p.left;
    }
    return p;
  }

  public getRootNode(): RedBlackTreeNode<V> | null {
    return this.m_Root;
  }

  public getLastNode(): RedBlackTreeNode<V> | null {
    let p = this.m_Root;
    if (p === null) {
      return null;
    }
    while (p.right !== null) {
      p = p.right;
    }
    return p;
  }

  public compareValues(v1: V, v2: V): number {
    if (this.m_Comparator !== null) {
      return this.m_Comparator.compare(v1, v2);
    } else {
      const vc1 = v1 as unknown as Comparable<V>;
      return vc1.compareTo(v2);
    }
  }

  public getCeilingNode(value: V): RedBlackTreeNode<V> | null {
    let p = this.m_Root;
    while (p !== null) {
      const cmp = this.compareValues(value, p.value);
      if (cmp < 0) {
        if (p.left !== null) {
          p = p.left;
        } else {
          return p;
        }
      } else if (cmp > 0) {
        if (p.right !== null) {
          p = p.right;
        } else {
          let parent = p.parent;
          let child = p;
          while (parent !== null && child === parent.right) {
            child = parent;
            parent = parent.parent;
          }
          return parent;
        }
      } else {
        return p;
      }
    }
    return null;
  }

  public getFloorNode(value: V): RedBlackTreeNode<V> | null {
    let p = this.m_Root;
    while (p !== null) {
      const cmp = this.compareValues(value, p.value);
      if (cmp > 0) {
        if (p.right !== null) {
          p = p.right;
        } else {
          return p;
        }
      } else if (cmp < 0) {
        if (p.left !== null) {
          p = p.left;
        } else {
          let parent = p.parent;
          let child = parent;
          while (parent !== null && child === parent.left) {
            child = parent;
            parent = parent.parent;
          }
          return parent;
        }
      } else {
        return p;
      }
    }
    return null;
  }

  public getHigherNode(value: V): RedBlackTreeNode<V> | null {
    let p = this.m_Root;
    while (p !== null) {
      const cmp = this.compareValues(value, p.value);
      if (cmp < 0) {
        if (p.left !== null) p = p.left;
        else return p;
      } else {
        if (p.right !== null) {
          p = p.right;
        } else {
          let parent = p.parent;
          let ch = p;
          while (parent !== null && ch === parent.right) {
            ch = parent;
            parent = parent.parent;
          }
          return parent;
        }
      }
    }
    return null;
  }

  public getLowerNode(value: V): RedBlackTreeNode<V> | null {
    let p = this.m_Root;
    while (p !== null) {
      const cmp = this.compareValues(value, p.value);
      if (cmp > 0) {
        if (p.right !== null) p = p.right;
        else return p;
      } else {
        if (p.left !== null) {
          p = p.left;
        } else {
          let parent = p.parent;
          let ch = p;
          while (parent !== null && ch === parent.left) {
            ch = parent;
            parent = parent.parent;
          }
          return parent;
        }
      }
    }
    return null;
  }

  public isEmpty(): boolean {
    return this.m_Root !== null;
  }

  public size(): number {
    return this.m_Size;
  }

  public getRedBlackTreeNode(value: V): RedBlackTreeNode<V> | null {
    if (this.m_Comparator !== null) {
      return this.getNodeUsingComparator(value);
    }
    if (value === null) {
      throw new NullPointerException();
    }
    const v = value as unknown as Comparable<V>;
    let p = this.m_Root;
    while (p !== null) {
      const cmp = v.compareTo(p.value);
      if (cmp < 0) {
        p = p.left;
      } else if (cmp > 0) {
        p = p.right;
      } else {
        return p;
      }
    }
    return null;
  }

  private getNodeUsingComparator(value: V): RedBlackTreeNode<V> | null {
    const cpr = this.m_Comparator;
    if (cpr !== null) {
      let p = this.m_Root;
      while (p !== null) {
        const cmp = cpr.compare(value, p.value);
        if (cmp < 0) {
          p = p.left;
        } else if (cmp > 0) {
          p = p.right;
        } else {
          return p;
        }
      }
    }
    return null;
  }

  private colorOf(node: RedBlackTreeNode<V> | null): RedBlackColor {
    if (node === null) {
      return RedBlackColor.BLACK;
    }
    return node.color;
  }

  private setColor(node: RedBlackTreeNode<V> | null, color: RedBlackColor): void {
    if (node !== null) {
      node.color = color;
    }
  }

  private leftOf(node: RedBlackTreeNode<V> | null): RedBlackTreeNode<V> | null {
    return node === null ? null : node.left;
  }

  private rightOf(node: RedBlackTreeNode<V> | null): RedBlackTreeNode<V> | null {
    return node === null ? null : node.right;
  }

  private parentOf(node: RedBlackTreeNode<V> | null): RedBlackTreeNode<V> | null {
    return node === null ? null : node.parent;
  }

  private rotateLeft(node: RedBlackTreeNode<V> | null): void {
    const p = node;
    if (p !== null && p.right !== null) {
      const r = p.right;
      p.right = r.left;
      if (r.left !== null) r.left.parent = p;
      r.parent = p.parent;
      if (p.parent === null) this.m_Root = r;
      else if (p.parent.left === p) p.parent.left = r;
      else p.parent.right = r;
      r.left = p;
      p.parent = r;
    }
  }

  private rotateRight(node: RedBlackTreeNode<V> | null): void {
    const p = node;
    if (p !== null && p.left !== null) {
      const l = p.left;
      p.left = l.right;
      if (l.right !== null) l.right.parent = p;
      l.parent = p.parent;
      if (p.parent === null) this.m_Root = l;
      else if (p.parent.right === p) p.parent.right = l;
      else p.parent.left = l;
      l.right = p;
      p.parent = l;
    }
  }

  private fixAfterInsertion(x: RedBlackTreeNode<V>): void {
    x.color = RedBlackColor.RED;
    let current: RedBlackTreeNode<V> | null = x;
    while (current !== null && current !== this.m_Root && this.colorOf(this.parentOf(current)) === RedBlackColor.RED) {
      if (this.parentOf(current) === this.leftOf(this.parentOf(this.parentOf(current)))) {
        const y = this.rightOf(this.parentOf(this.parentOf(current)));
        if (this.colorOf(y) === RedBlackColor.RED) {
          this.setColor(this.parentOf(current), RedBlackColor.BLACK);
          this.setColor(y, RedBlackColor.BLACK);
          this.setColor(this.parentOf(this.parentOf(current)), RedBlackColor.RED);
          current = this.parentOf(this.parentOf(current));
        } else {
          if (current === this.rightOf(this.parentOf(current))) {
            current = this.parentOf(current) ?? current;
            this.rotateLeft(current);
          }
          this.setColor(this.parentOf(current), RedBlackColor.BLACK);
          this.setColor(this.parentOf(this.parentOf(current)), RedBlackColor.RED);
          this.rotateRight(this.parentOf(this.parentOf(current)));
        }
      } else {
        const y = this.leftOf(this.parentOf(this.parentOf(current)));
        if (this.colorOf(y) === RedBlackColor.RED) {
          this.setColor(this.parentOf(current), RedBlackColor.BLACK);
          this.setColor(y, RedBlackColor.BLACK);
          this.setColor(this.parentOf(this.parentOf(current)), RedBlackColor.RED);
          current = this.parentOf(this.parentOf(current));
        } else {
          if (current === this.leftOf(this.parentOf(current))) {
            current = this.parentOf(current) ?? current;
            this.rotateRight(current);
          }
          this.setColor(this.parentOf(current), RedBlackColor.BLACK);
          this.setColor(this.parentOf(this.parentOf(current)), RedBlackColor.RED);
          this.rotateLeft(this.parentOf(this.parentOf(current)));
        }
      }
    }
    if (this.m_Root) {
      this.m_Root.color = RedBlackColor.BLACK;
    }
  }

  public successor(t: RedBlackTreeNode<V> | null): RedBlackTreeNode<V> | null {
    if (t === null) return null;
    else if (t.right !== null) {
      let p = t.right;
      while (p.left !== null) p = p.left;
      return p;
    } else {
      let p = t.parent;
      let ch = t;
      while (p !== null && ch === p.right) {
        ch = p;
        p = p.parent;
      }
      return p;
    }
  }

  /*public static key<K,V>(entry: TreeMapEntry<K, V>): K {
          if(entry === null){
               throw new NoSuchElementException();
          }
          return entry.getKey();
     }*/

  public static entryOrNull<V>(node: RedBlackTreeNode<V> | null): V | null {
    return node === null ? null : node.value;
  }

  /**
   * Returns the predecessor of the specified Entry, or null if no such.
   */
  public predecessor(t: RedBlackTreeNode<V> | null): RedBlackTreeNode<V> | null {
    if (t === null) return null;
    else if (t.left !== null) {
      let p = t.left;
      while (p.right !== null) p = p.right;
      return p;
    } else {
      let p = t.parent;
      let ch = t;
      while (p !== null && ch === p.left) {
        ch = p;
        p = p.parent;
      }
      return p;
    }
  }

  /**
   * Delete node p, and then rebalance the tree.
   */
  public deleteEntry(p: RedBlackTreeNode<V>): void {
    // If strictly internal, copy successor's element to p and then make p
    // point to successor.
    this.m_Size--;
    this.modCount++;
    if (p.left !== null && p.right !== null) {
      const s = this.successor(p);
      if (s) {
        p.value = s.value;
        p = s;
      }
    } // p has 2 children

    // Start fixup at replacement node, if it exists.
    const replacement = p.left !== null ? p.left : p.right;

    if (replacement !== null) {
      // Link replacement to parent
      replacement.parent = p.parent;
      if (p.parent === null) this.m_Root = replacement;
      else if (p === p.parent.left) p.parent.left = replacement;
      else p.parent.right = replacement;

      // Null out links so they are OK to use by fixAfterDeletion.
      p.left = p.right = p.parent = null;

      // Fix replacement
      if (p.color === RedBlackColor.BLACK) this.fixAfterDeletion(replacement);
    } else if (p.parent === null) {
      // return if we are the only node.
      this.m_Root = null;
    } else {
      //  No children. Use self as phantom replacement and unlink.
      if (p.color === RedBlackColor.BLACK) this.fixAfterDeletion(p);

      if (p.parent !== null) {
        if (p === p.parent.left) p.parent.left = null;
        else if (p === p.parent.right) p.parent.right = null;
        p.parent = null;
      }
    }
  }

  /** From CLR */
  private fixAfterDeletion(x: RedBlackTreeNode<V>): void {
    let current: RedBlackTreeNode<V> | null = x;
    while (current !== this.m_Root && this.colorOf(current) === RedBlackColor.BLACK) {
      if (current === this.leftOf(this.parentOf(current))) {
        let sib = this.rightOf(this.parentOf(current));

        if (this.colorOf(sib) === RedBlackColor.RED) {
          this.setColor(sib, RedBlackColor.BLACK);
          this.setColor(this.parentOf(current), RedBlackColor.RED);
          this.rotateLeft(this.parentOf(current));
          sib = this.rightOf(this.parentOf(current));
        }

        if (this.colorOf(this.leftOf(sib)) === RedBlackColor.BLACK && this.colorOf(this.rightOf(sib)) === RedBlackColor.BLACK) {
          this.setColor(sib, RedBlackColor.RED);
          current = this.parentOf(current);
        } else {
          if (this.colorOf(this.rightOf(sib)) === RedBlackColor.BLACK) {
            this.setColor(this.leftOf(sib), RedBlackColor.BLACK);
            this.setColor(sib, RedBlackColor.RED);
            this.rotateRight(sib);
            sib = this.rightOf(this.parentOf(current));
          }
          this.setColor(sib, this.colorOf(this.parentOf(current)));
          this.setColor(this.parentOf(current), RedBlackColor.BLACK);
          this.setColor(this.rightOf(sib), RedBlackColor.BLACK);
          this.rotateLeft(this.parentOf(current));
          current = this.m_Root;
        }
      } else {
        // symmetric
        let sib = this.leftOf(this.parentOf(current));

        if (this.colorOf(sib) === RedBlackColor.RED) {
          this.setColor(sib, RedBlackColor.BLACK);
          this.setColor(this.parentOf(current), RedBlackColor.RED);
          this.rotateRight(this.parentOf(current));
          sib = this.leftOf(this.parentOf(current));
        }

        if (this.colorOf(this.rightOf(sib)) === RedBlackColor.BLACK && this.colorOf(this.leftOf(sib)) === RedBlackColor.BLACK) {
          this.setColor(sib, RedBlackColor.RED);
          current = this.parentOf(current);
        } else {
          if (this.colorOf(this.leftOf(sib)) === RedBlackColor.BLACK) {
            this.setColor(this.rightOf(sib), RedBlackColor.BLACK);
            this.setColor(sib, RedBlackColor.RED);
            this.rotateLeft(sib);
            sib = this.leftOf(this.parentOf(current));
          }
          this.setColor(sib, this.colorOf(this.parentOf(current)));
          this.setColor(this.parentOf(current), RedBlackColor.BLACK);
          this.setColor(this.leftOf(sib), RedBlackColor.BLACK);
          this.rotateRight(this.parentOf(current));
          current = this.m_Root;
        }
      }
    }
    this.setColor(current, RedBlackColor.BLACK);
  }
}
